import { useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { consumeTraverse, getRouteY, resolveScreenTop, trackScrollY } from '@/utils/screenTop'

/**
 * Scrolls back under the banner when the screen changes (mock screenTop). `key` names the screen
 * (`null` disables the hook); `getAnchor()` returns the sticky element to land under (its computed
 * `top` is the offset), or nothing to go to 0. The first key seen is only remembered; browser back /
 * forward keep the restored position.
 */
export default function useScreenTop(key, getAnchor) {
    const location = useLocation()
    const lastKey = useRef(null)
    const anchorRef = useRef(getAnchor)
    anchorRef.current = getAnchor

    trackScrollY()

    // every location change, so a back / forward flag is consumed even when the screen key stays
    useLayoutEffect(() => {
        const traverse = consumeTraverse()
        if (key === null || key === undefined) return
        const changed = lastKey.current !== null && lastKey.current !== key
        lastKey.current = key
        if (!changed || traverse) return

        // routeY: the position before this screen rendered (a shorter document already clamped scrollY)
        const routeY = Math.max(getRouteY(), window.scrollY)
        window.scrollTo(0, 0)
        const anchor = anchorRef.current?.()
        if (!anchor) return

        // the anchor measures once the whole route is applied — a microtask, before the paint
        Promise.resolve().then(() => {
            if (window.scrollY !== 0 || !anchor.offsetHeight) return
            const top = anchor.getBoundingClientRect().top
            const sticky = parseFloat(getComputedStyle(anchor).top) || 0
            const y = resolveScreenTop({
                changed: true,
                traverse: false,
                routeY,
                anchorTop: top,
                anchorStickyTop: sticky,
            })
            if (y !== null) window.scrollTo(0, y)
        })
    }, [key, location.key])
}
